"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  HIGHLIGHT_TONES,
  HighlightedText,
  type TextMatch,
} from "@/components/tool-kit/highlighted-text";
import {
  CopyButton,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type AnthropomorphismFinding,
  findAnthropomorphism,
  type SubjectKind,
  type Tier,
} from "@/lib/tool-kit/style-checkers/anthropomorphism";

/** English strings. A translated content file overrides these through `ui`. */
export const ANTHROPOMORPHISM_CHECKER_STRINGS = {
  textLabel: "Your text",
  placeholder: "Paste a paragraph, a section or a whole paper…",
  privacy: "Runs entirely in your browser — your text is never uploaded.",
  tryExample: "Try an example",
  clear: "Clear",
  exampleText:
    "The study concluded that daily naps improve recall. This paper argues that schools should start later, and the theory wants to explain why. Our experiment attempted to replicate the effect. The results suggest a small benefit, and Table 2 shows the means. The company believes that its staff sleep well.",
  modeLabel: "What to list",
  modeStandard: "Standard — leave widely accepted wording alone",
  modeStrict: "Strict — also list widely accepted wording",
  enterPrompt:
    "Paste or type some text to check whether studies, papers, theories and tables are doing things only people can do.",
  results: "Results",
  words: "Words",
  likely: "Likely anthropomorphism",
  check: "Worth checking",
  accepted: "Widely accepted",
  readingNone:
    "No study, paper, theory, table or organisation is shown thinking, arguing or concluding. That does not prove the wording is right — the check covers a fixed list of verbs and nouns.",
  readingSome:
    "Each item below is a sentence where a non-human subject does something a person does. Decide whether to name the person, change the verb, or keep it.",
  acceptedLeft:
    "one:{count} widely accepted construction, such as “the results suggest”, was left alone. Switch to Strict to list it.|other:{count} widely accepted constructions, such as “the results suggest”, were left alone. Switch to Strict to list them.",
  highlightedHeading: "Your text, highlighted",
  highlightLabel: "Your text with anthropomorphic wording highlighted",
  legendLikely: "Likely",
  legendCheck: "Worth checking",
  legendAccepted: "Widely accepted",
  listHeading: "What was found",
  tierLikely: "Likely anthropomorphism",
  tierCheck: "Worth checking",
  tierAccepted: "Widely accepted",
  adviceLikely:
    "Only a person can {verb}. Name who did — the researchers, the authors, the participants — or reword so the subject is a person.",
  adviceCheck:
    "Many fields reserve “{verb}” for people. Texts often get away with it; for APA-safe wording, name the person or use a verb that is literally true of the source.",
  adviceAccepted:
    "Construction in widespread use that APA accepts: it does not mislead the reader. Listed only because you chose Strict.",
  hintResearch:
    "A study is a piece of work; its authors are the ones who reason.",
  hintText: "A text contains arguments; its author makes them.",
  hintTheory:
    "A theory does not hold beliefs; the people who propose it do. “Assumes” and “predicts” are ordinary technical wording for a model.",
  hintDisplay:
    "A table or figure can show, list or present. It cannot argue or decide.",
  hintEvidence:
    "Results can indicate or suggest. They do not conclude; people draw conclusions from them.",
  hintOrganization:
    "A company or school can act as a unit. Name the people when it matters who decided.",
  rewriteLabel: "Try",
  copySuggestion: "Copy",
  copied: "Copied!",
  literalLabel: "Or use a verb the source can really do:",
  noRewrite: "No person fits here — change the verb instead.",
  limits:
    "The checker looks for a fixed list of subject nouns followed by a fixed list of verbs. It cannot tell that “the group” means people, skips quoted wording, and does not check animals. A flagged phrase is a question for you, not an error.",
} as const;

type Strings = typeof ANTHROPOMORPHISM_CHECKER_STRINGS;
type Mode = "standard" | "strict";

const TONES = { likely: "rose", check: "amber", accepted: "sky" } as const;

const HINT_KEYS: Record<SubjectKind, keyof Strings> = {
  research: "hintResearch",
  text: "hintText",
  theory: "hintTheory",
  display: "hintDisplay",
  evidence: "hintEvidence",
  organization: "hintOrganization",
};

function tierLabel(tier: Tier, strings: Strings): string {
  if (tier === "likely") {
    return strings.tierLikely;
  }
  return tier === "check" ? strings.tierCheck : strings.tierAccepted;
}

function advice(tier: Tier, verb: string, strings: Strings): string {
  if (tier === "likely") {
    return t(strings.adviceLikely, { verb });
  }
  return tier === "check"
    ? t(strings.adviceCheck, { verb })
    : strings.adviceAccepted;
}

const BADGE: Record<Tier, string> = {
  likely: "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200",
  check: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200",
  accepted: "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200",
};

function FindingCard({
  finding,
  strings,
}: {
  finding: AnthropomorphismFinding;
  strings: Strings;
}) {
  return (
    <li className={`${CARD} space-y-3`}>
      <div className="flex flex-wrap items-center gap-2">
        <code className="rounded bg-muted px-2 py-1 text-foreground text-sm">
          {finding.text}
        </code>
        <span
          className={`rounded-full px-2 py-0.5 font-medium text-xs ${BADGE[finding.tier]}`}
        >
          {tierLabel(finding.tier, strings)}
        </span>
      </div>
      <p className="text-muted-foreground text-sm leading-relaxed">
        {advice(finding.tier, finding.verbBase, strings)}{" "}
        {strings[HINT_KEYS[finding.kind]]}
      </p>
      {finding.tier === "accepted" ? null : (
        <div className="space-y-2">
          {finding.suggestion ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-foreground text-sm">
                {strings.rewriteLabel}
              </span>
              <code className="rounded bg-muted px-2 py-1 text-foreground text-sm">
                {finding.suggestion}
              </code>
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copySuggestion}
                small
                text={finding.suggestion}
              />
            </div>
          ) : (
            <p className={HINT}>{strings.noRewrite}</p>
          )}
          {finding.literal.length > 0 ? (
            <p className={HINT}>
              {strings.literalLabel} {finding.literal.join(" · ")}
            </p>
          ) : null}
        </div>
      )}
    </li>
  );
}

export default function AnthropomorphismCheckerWidget() {
  const strings = useToolUi(ANTHROPOMORPHISM_CHECKER_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const [text, setText] = useState("");
  const [mode, setMode] = useState<Mode>("standard");
  const deferred = useDeferredValue(text);
  const report = useMemo(() => findAnthropomorphism(deferred), [deferred]);

  const shown = report.findings.filter(
    (finding) => mode === "strict" || finding.tier !== "accepted"
  );
  const highlights: TextMatch[] = shown.map((finding) => ({
    start: finding.start,
    end: finding.end,
    category: finding.tier,
    title: tierLabel(finding.tier, strings),
  }));
  const hasText = deferred.trim().length > 0;

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.privacy}
        id="ac-text"
        label={strings.textLabel}
        onChange={setText}
        placeholder={strings.placeholder}
        prose
        rows={10}
        value={text}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setText(strings.exampleText)}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text.length === 0}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <PillGroup
        label={strings.modeLabel}
        name="ac-mode"
        onChange={setMode}
        options={[
          { value: "standard", label: strings.modeStandard },
          { value: "strict", label: strings.modeStrict },
        ]}
        value={mode}
      />

      {hasText ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite" className="space-y-4">
            <StatGrid>
              <StatCard
                emphasis
                label={strings.likely}
                value={fmt(report.counts.likely)}
              />
              <StatCard
                emphasis
                label={strings.check}
                value={fmt(report.counts.check)}
              />
              <StatCard
                label={strings.accepted}
                value={fmt(report.counts.accepted)}
              />
              <StatCard label={strings.words} value={fmt(report.words)} />
            </StatGrid>
            <Notice>
              {shown.length === 0 ? strings.readingNone : strings.readingSome}
            </Notice>
            {mode === "standard" && report.counts.accepted > 0 ? (
              <p className={HINT}>
                {tPlural(strings.acceptedLeft, locale, report.counts.accepted, {
                  count: fmt(report.counts.accepted),
                })}
              </p>
            ) : null}
          </div>

          <div className="space-y-3">
            <h3 className={WIDGET_H3}>{strings.highlightedHeading}</h3>
            <p className="flex flex-wrap gap-3 text-sm">
              <span className={HIGHLIGHT_TONES.rose}>
                {strings.legendLikely}
              </span>
              <span className={HIGHLIGHT_TONES.amber}>
                {strings.legendCheck}
              </span>
              {mode === "strict" ? (
                <span className={HIGHLIGHT_TONES.sky}>
                  {strings.legendAccepted}
                </span>
              ) : null}
            </p>
            <HighlightedText
              label={strings.highlightLabel}
              matches={highlights}
              text={deferred}
              tones={TONES}
            />
          </div>

          {shown.length > 0 ? (
            <div className="space-y-3">
              <h3 className={WIDGET_H3}>{strings.listHeading}</h3>
              <ul className="space-y-3">
                {shown.map((finding) => (
                  <FindingCard
                    finding={finding}
                    key={finding.start}
                    strings={strings}
                  />
                ))}
              </ul>
            </div>
          ) : null}
          <p className={HINT}>{strings.limits}</p>
        </section>
      ) : (
        <Notice>{strings.enterPrompt}</Notice>
      )}
    </div>
  );
}
